import { t } from "i18next";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { Button, Form, Table } from "react-bootstrap";
import * as htmlToImage from 'html-to-image';
import moment from 'moment';
import 'moment/locale/es';
import { Season } from "../../../../interfaces/season";
import { getSeasonOptions, getSeasonCurrent } from "../../../../services/season";
import { LoadingSpinner } from "../../../general/loading-spinner";
import { getTeamOptions } from "../../../../services/team";
import { Team } from "../../../../interfaces/team";
import { getPlanning, updatePlanningWeek, updatePlanningPeriodicity, createPlanningWeekContent, updatePlanningWeekContent, pastePlanningWeek, clearPlanningWeek, exportAnnualPlanningPDF } from "../../../../services/planning";
import { emptyPlanning, Planning, PlanningDay, PlanningDayValue, planningLevels, PlanningMonth, PlanningWeek, Macrocycle, Period, Mesocycle, PlanningRace, contentDistributionFields, PlanningWeekContent } from "../../../../interfaces/planning";
import CoachPlanningWeek from "./CoachPlanningWeek";
import { Club } from "../../../../interfaces/club";
import { getClubOptions } from "../../../../services/club";
import { MacrocycleModal } from "./MacrocycleModal";
import { PeriodModal } from "./PeriodModal";
import { MesocycleModal } from "./MesocycleModal";
import { PlanningRaceModal } from "./PlanningRaceModal";
import { ContentEditModal } from "./ContentEditModal";
import { TotalSessionsModal } from "./TotalSessionsModal";
import WeeklyPlanningModal from "./WeeklyPlanningModal";

// Translate month names in planning data from English to Spanish using moment
const translatePlanningMonths = (planning: Planning): Planning => {
    // Set moment locale to Spanish
    moment.locale('es');

    return {
        ...planning,
        months: planning.months.map((month: PlanningMonth) => {
            // Parse the key format "MMM-YY" (e.g., "Sep-25", "Dec-25")
            // Use the key to determine the month, not the from date
            // (from date can be a Monday from the previous month)
            let translatedName = month.name;

            const match = month.key.match(/^([A-Za-z]+)[\s\-](\d+)$/);
            if (match) {
                // Parse "Sep-25" as "Sep 2025" using moment in English
                const monthDate = moment(month.key, 'MMM-YY', 'en');
                if (monthDate.isValid()) {
                    // Format as "MMM-YY" in current locale (e.g., "sep-25")
                    translatedName = monthDate.format('MMM-YY').replace('.', '');
                }
            }

            return {
                ...month,
                name: translatedName
            };
        })
    };
};

function CoachPlanning() {
    const [loaded, setLoaded] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [planning, setPlanning] = useState<Planning>(emptyPlanning);
    const [showWeekPlanner, setShowWeekPlanner] = useState(false);
    const [selectedWeek, setSelectedWeek] = useState<PlanningWeek>();
    const [seasonOptions, setSeasonOptions] = useState<Season[]>([]);
    const [selectedSeasonId, setSelectedSeasonId] = useState(0);
    const [selectedSeasonName, setSelectedSeasonName] = useState('');
    const [clubOptions, setClubOptions] = useState<Club[]>([]);
    const [selectedClubId, setSelectedClubId] = useState(0);
    const [selectedClubName, setSelectedClubName] = useState('');
    const [teamOptions, setTeamOptions] = useState<Team[]>([]);
    const [selectedTeamId, setSelectedTeamId] = useState(0);
    const [selectedTeamName, setSelectedTeamName] = useState('');

    // Macrocycle states
    const [macrocycles, setMacrocycles] = useState<Macrocycle[]>([]);
    const [periods, setPeriods] = useState<Period[]>([]);
    const [mesocycles, setMesocycles] = useState<Mesocycle[]>([]);
    const [selectedWeeks, setSelectedWeeks] = useState<string[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [showMacrocycleModal, setShowMacrocycleModal] = useState(false);
    const [editingMacrocycle, setEditingMacrocycle] = useState<Macrocycle | null>(null);
    const [hoveredMacrocycle, setHoveredMacrocycle] = useState<string | null>(null);

    // Period states
    const [selectedWeeksPeriod, setSelectedWeeksPeriod] = useState<string[]>([]);
    const [isDraggingPeriod, setIsDraggingPeriod] = useState(false);
    const [showPeriodModal, setShowPeriodModal] = useState(false);
    const [editingPeriod, setEditingPeriod] = useState<Period | null>(null);
    const [hoveredPeriod, setHoveredPeriod] = useState<string | null>(null);

    // Mesocycle states
    const [selectedWeeksMesocycle, setSelectedWeeksMesocycle] = useState<string[]>([]);
    const [isDraggingMesocycle, setIsDraggingMesocycle] = useState(false);
    const [showMesocycleModal, setShowMesocycleModal] = useState(false);
    const [editingMesocycle, setEditingMesocycle] = useState<Mesocycle | null>(null);
    const [hoveredMesocycle, setHoveredMesocycle] = useState<string | null>(null);

    /**
     * Race management states
     * - showRaceModal: Controls visibility of the race management modal
     * - selectedRaceWeek: Stores the week being edited in the modal
     */
    const [showRaceModal, setShowRaceModal] = useState(false);
    const [selectedRaceWeek, setSelectedRaceWeek] = useState<PlanningWeek | null>(null);

    /**
     * Content distribution states
     * - showContentModal: Controls visibility of the content edit modal
     * - selectedContentWeek: Stores the week being edited
     * - selectedContentField: Stores the field key being edited
     * - selectedContentFieldName: Stores the field display name
     */
    const [showContentModal, setShowContentModal] = useState(false);
    const [selectedContentWeek, setSelectedContentWeek] = useState<PlanningWeek | null>(null);
    const [selectedContentField, setSelectedContentField] = useState<string>('');
    const [selectedContentFieldName, setSelectedContentFieldName] = useState<string>('');

    /**
     * Total sessions states
     * - showTotalSessionsModal: Controls visibility of the total sessions modal
     * - selectedSessionsWeek: Stores the week being edited
     */
    const [showTotalSessionsModal, setShowTotalSessionsModal] = useState(false);
    const [selectedSessionsWeek, setSelectedSessionsWeek] = useState<PlanningWeek | null>(null);

    /**
     * PDF export state
     */
    const [exportingAnnualPDF, setExportingAnnualPDF] = useState(false);
    const planningTableRef = useRef<HTMLDivElement>(null);
    const topScrollRef = useRef<HTMLDivElement>(null);
    const topScrollInnerRef = useRef<HTMLDivElement>(null);
    const isSyncingScroll = useRef(false);

    // Sync top scrollbar width with table scroll width
    useEffect(() => {
        const tableEl = planningTableRef.current;
        const innerEl = topScrollInnerRef.current;
        if (!tableEl || !innerEl) return;

        const updateWidth = () => {
            innerEl.style.width = tableEl.scrollWidth + 'px';
        };

        updateWidth();
        const observer = new ResizeObserver(updateWidth);
        observer.observe(tableEl);
        return () => observer.disconnect();
    }, [planning]);

    // Sync scroll between top scrollbar and table
    const handleTopScroll = useCallback(() => {
        if (isSyncingScroll.current) return;
        isSyncingScroll.current = true;
        if (planningTableRef.current && topScrollRef.current) {
            planningTableRef.current.scrollLeft = topScrollRef.current.scrollLeft;
        }
        isSyncingScroll.current = false;
    }, []);

    const handleTableScroll = useCallback(() => {
        if (isSyncingScroll.current) return;
        isSyncingScroll.current = true;
        if (topScrollRef.current && planningTableRef.current) {
            topScrollRef.current.scrollLeft = planningTableRef.current.scrollLeft;
        }
        isSyncingScroll.current = false;
    }, []);

    /**
     * Copy/Paste week state with context menu
     */
    const [copiedWeekRef, setCopiedWeekRef] = useState<{ season_id: number, team_id: number, week_key: string } | null>(null);
    const [contextMenuVisible, setContextMenuVisible] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    const [contextMenuWeek, setContextMenuWeek] = useState<{ month: string, week: string } | null>(null);

    /**
     * Copy/Paste day state (persists across week modal openings)
     */
    const [copiedDayRef, setCopiedDayRef] = useState<{ season_id: number, team_id: number, week_key: string, day_key: string } | null>(null);

    /**
     * Weekly planning modal state
     */
    const [showWeeklyModal, setShowWeeklyModal] = useState(false);
    const [weeklyModalParams, setWeeklyModalParams] = useState<{ seasonId: number, teamId: number, weekKey: string } | null>(null);

    useEffect(() => {
        Promise.all([
            getSeasonOptions(),
            getSeasonCurrent()
        ]).then((response: any) => {
            setSeasonOptions(response[0].data);
            const currentSeason = response[1].data;

            // Set current season as default if it exists
            if (currentSeason && currentSeason.id) {
                setSelectedSeasonId(currentSeason.id);

                // Load clubs for the current season
                getClubOptions('', currentSeason.id)
                    .then((clubResponse: any) => {
                        setClubOptions(clubResponse.data);
                    })
                    .catch((error: any) => {
                        console.error('Error loading clubs:', error);
                    });
            }

            setLoaded(true);
        }).catch((error: any) => {
            console.error('Error loading seasons:', error);
            setLoaded(true);
        });
    }, []);

    // Close context menu when clicking anywhere
    useEffect(() => {
        const handleClickOutside = () => {
            if (contextMenuVisible) {
                setContextMenuVisible(false);
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [contextMenuVisible]);
    
    const handleSearchSubmit = (event: any) => {
        event.preventDefault();

        const seasonIndex = seasonOptions.findIndex((s: Season) => s.id == event.target.season.value);
        if (seasonIndex === -1) {
            setSelectedSeasonId(0);
            setSelectedSeasonName('');
        } else {
            setSelectedSeasonId(seasonOptions[seasonIndex].id);
            setSelectedSeasonName(seasonOptions[seasonIndex].name);
        }

        const clubIndex = clubOptions.findIndex((t: Club) => t.id == event.target.club.value);
        if (clubIndex === -1) {
            setSelectedClubId(0);
            setSelectedClubName('');
        } else {
            setSelectedClubId(clubOptions[clubIndex].id);
            setSelectedClubName(clubOptions[clubIndex].name);
        }

        const teamIndex = teamOptions.findIndex((t: Team) => t.id == event.target.team.value);
        if (teamIndex === -1) {
            setSelectedTeamId(0);
            setSelectedTeamName('');
        } else {
            setSelectedTeamId(teamOptions[teamIndex].id);
            setSelectedTeamName(teamOptions[teamIndex].name);
        }

        setPlanning(emptyPlanning);
        setMacrocycles([]);
        setPeriods([]);
        setMesocycles([]);
        setRefreshing(true);

        const season = event.target.season.value;
        const team = event.target.team.value;

        getPlanning(season, team).then((response: any) => {
                // Translate month names to Spanish
                const translatedPlanning = translatePlanningMonths(response.data);
                setPlanning(translatedPlanning);

                // Load macrocycles, periods, and mesocycles if they exist
                const macrocyclesData = response.data.macrocycles || [];
                const periodsData = response.data.periods || [];
                const mesocyclesData = response.data.mesocycles || [];

                // Handle both array and object formats
                const macrocyclesArray = (Array.isArray(macrocyclesData) ? macrocyclesData : Object.values(macrocyclesData)).map((mc: any) => {
                    // Ensure weeks is an array, converting from object if necessary
                    const weeks = Array.isArray(mc.weeks) ? mc.weeks : Object.values(mc.weeks || {});
                    return {
                        ...mc,
                        weeks: weeks as string[]
                    };
                }) as Macrocycle[];
                setMacrocycles(macrocyclesArray);

                const periodsArray = (Array.isArray(periodsData) ? periodsData : Object.values(periodsData)).map((p: any) => {
                    const weeks = Array.isArray(p.weeks) ? p.weeks : Object.values(p.weeks || {});
                    return {
                        ...p,
                        weeks: weeks as string[]
                    };
                }) as Period[];
                setPeriods(periodsArray);

                const mesocyclesArray = (Array.isArray(mesocyclesData) ? mesocyclesData : Object.values(mesocyclesData)).map((m: any) => {
                    const weeks = Array.isArray(m.weeks) ? m.weeks : Object.values(m.weeks || {});
                    return {
                        ...m,
                        weeks: weeks as string[]
                    };
                }) as Mesocycle[];
                setMesocycles(mesocyclesArray);

                // Races are already included in each week from the backend
                // No need to integrate them manually

                setRefreshing(false);
            });
    }

    const handleShowWeekPlanner = (event: any) => {
        const monthKey = event.currentTarget.dataset.month;
        const weekKey = event.currentTarget.dataset.week;

        const monthIndex = planning.months.findIndex((m: PlanningMonth) => m.key == monthKey);
        if (monthIndex !== -1) {
            const weekIndex = planning.months[monthIndex].weeks.findIndex((w: PlanningWeek) => w.key == weekKey);
            if (weekIndex !== -1) {
                setSelectedWeek(planning.months[monthIndex].weeks[weekIndex]);
                setShowWeekPlanner(true);
            }
        }
    }

    const handleHideWeekPlanner = () => {
        setSelectedWeek(undefined);
        setShowWeekPlanner(false);
    }

    /**
     * Refreshes planning data and updates the selectedWeek in the week planner modal
     * Used after copy/paste day operations
     */
    const handleRefreshWeekPlanner = () => {
        getPlanning(selectedSeasonId, selectedTeamId).then((planningResponse: any) => {
            const translatedPlanning = translatePlanningMonths(planningResponse.data);
            setPlanning(translatedPlanning);

            // Update selectedWeek with the refreshed data
            if (selectedWeek) {
                for (const month of translatedPlanning.months) {
                    const updatedWeek = month.weeks.find((w: PlanningWeek) => w.key === selectedWeek.key);
                    if (updatedWeek) {
                        setSelectedWeek(updatedWeek);
                        break;
                    }
                }
            }
        }).catch((error: any) => {
            console.error('Error refreshing planning:', error);
        });
    }

    const handleSaveWorkload = (week: string, workload: number) => {
        const newPlanning = Object.assign({}, planning);
        for (let i = 0; i < newPlanning.months.length; i++) {
            const weekIndex = newPlanning.months[i].weeks.findIndex((w: PlanningWeek) => w.key == week);
            if (weekIndex !== -1) {
                newPlanning.months[i].weeks[weekIndex].workload = workload;
            }
        }
        setPlanning(newPlanning);
    }

    const handleSaveDay = (week: string, day: PlanningDay) => {
        const newPlanning = {
            ...planning,
            months: planning.months.map((month: PlanningMonth) => {
                const weekIndex = month.weeks.findIndex((w: PlanningWeek) => w.key == week);
                if (weekIndex === -1) {
                    return month;
                }

                const newWeeks = month.weeks.map((w: PlanningWeek, idx: number) => {
                    if (idx !== weekIndex) {
                        return w;
                    }

                    const newDays = w.days.map((d: PlanningDay) =>
                        d.key === day.key ? { ...day } : d
                    );

                    const updatedWeek = { ...w, days: newDays };

                    // Si es la semana seleccionada, actualizarla
                    if (selectedWeek && selectedWeek.key === week) {
                        setSelectedWeek(updatedWeek);
                    }

                    return updatedWeek;
                });

                return { ...month, weeks: newWeeks };
            })
        };

        setPlanning(newPlanning);
    }

    const handleWorkloadChange = (event: any) => {
        const team = event.currentTarget.dataset.team;
        const season = event.currentTarget.dataset.season;
        const week = event.currentTarget.dataset.week;
        const workload = event.currentTarget.dataset.workload;

        // Find the current total_sessions for this week
        let currentTotalSessions = 0;
        for (let i = 0; i < planning.months.length; i++) {
            const weekData = planning.months[i].weeks.find((w: PlanningWeek) => w.key == week);
            if (weekData && weekData.total_sessions) {
                currentTotalSessions = weekData.total_sessions;
                break;
            }
        }

        const body_obj = {
            team_id: team,
            season_id: season,
            week: week,
            workload: workload,
            total_sessions: currentTotalSessions
        }
        updatePlanningWeek(body_obj)
            .then(response => {
                if (response.data.status == 1) {
                    const newPlanning = Object.assign({}, planning);
                    for (let i = 0; i < newPlanning.months.length; i++) {
                        const weekIndex = newPlanning.months[i].weeks.findIndex((w: PlanningWeek) => w.key == week);
                        if (weekIndex !== -1) {
                            newPlanning.months[i].weeks[weekIndex].workload = workload;
                        }
                    }
                    setPlanning(newPlanning);
                } else {
                    alert(t("Data could not be saved. Try again later."));
                }
            });
    }

    const getWeekValue = (month: string, week: string, field: string) => {
        let output = 0;

        const monthIndex = planning.months.findIndex((m: PlanningMonth) => m.key == month);
        if (monthIndex !== -1) {
            const weekIndex = planning.months[monthIndex].weeks.findIndex((w: PlanningWeek) => w.key == week);
            if (weekIndex !== -1) {
                const days = planning.months[monthIndex].weeks[weekIndex].days;
                for (let dayIndex = 0; dayIndex < days.length; dayIndex++) {
                    const valueItems = planning.months[monthIndex].weeks[weekIndex].days[dayIndex].values.filter((v: PlanningDayValue) => v.field == field);
                    for (let valueItemIndex = 0; valueItemIndex < valueItems.length; valueItemIndex++) {
                        // Convert European comma decimal to period before parsing
                        let rawValue = valueItems[valueItemIndex].value;
                        if (rawValue && typeof rawValue === 'string') {
                            rawValue = rawValue.replace(',', '.');
                        }
                        const value = parseFloat(rawValue).toFixed(1);
                        if (value.toString() != 'NaN') {
                            output += parseFloat(value);
                        }
                    }
                }
            }
        }

        // Formatear a máximo 1 decimal
        return parseFloat(output.toFixed(1));
    }

    const handleSeasonChange = (event: any) => {
        const currentSeason = event.currentTarget.value;
        setSelectedSeasonId(currentSeason);
        setSelectedClubId(0);
        setSelectedTeamId(0);
        setClubOptions([]);
        setTeamOptions([]);
        setPlanning(emptyPlanning);
        if (currentSeason != 0) {
            getClubOptions('', currentSeason)
                .then((response: any) => {
                    setClubOptions(response.data);
                });
        }
    }

    const handleClubChange = (event: any) => {
        const currentClub = event.currentTarget.value;
        setSelectedClubId(currentClub);
        setSelectedTeamId(0);
        setTeamOptions([]);
        setPlanning(emptyPlanning);
        if (currentClub != 0) {
            getTeamOptions(selectedSeasonId, currentClub)
                .then((response: any) => {
                    setTeamOptions(response.data);
                });
        }
    }

    const handleTeamChange = (event: any) => {
        const currentTeam = event.currentTarget.value;
        setSelectedTeamId(currentTeam);
        setPlanning(emptyPlanning);
    }

    /**
     * Capture planning table as image
     */
    const capturePlanningTable = async (): Promise<string | null> => {
        if (!planningTableRef.current) {
            console.warn('Planning table ref not available');
            return null;
        }

        // Store original styles to restore later
        const element = planningTableRef.current;
        const originalOverflow = element.style.overflow;
        const originalMaxHeight = element.style.maxHeight;
        const originalHeight = element.style.height;
        const originalWidth = element.style.width;
        const originalPosition = element.style.position;

        try {
            // Temporarily remove scroll and expand to full content size
            element.style.overflow = 'visible';
            element.style.maxHeight = 'none';
            element.style.height = 'auto';
            element.style.width = 'max-content';
            element.style.position = 'relative';

            // Give browser time to reflow
            await new Promise(resolve => setTimeout(resolve, 100));

            // Use html-to-image to capture the table
            const dataUrl = await htmlToImage.toPng(element, {
                quality: 1.0,
                pixelRatio: 2, // Higher resolution for PDF
                backgroundColor: '#ffffff',
                cacheBust: true, // Ensure fresh capture
                skipFonts: true, // Skip web font processing to avoid font errors
                preferredFontFormat: 'woff2', // Use modern font format
                width: element.scrollWidth, // Capture full width
                height: element.scrollHeight, // Capture full height
                style: {
                    // Ensure all content is visible
                    transform: 'scale(1)',
                    transformOrigin: 'top left',
                    overflow: 'visible',
                    maxHeight: 'none',
                    height: 'auto'
                },
                filter: (node: HTMLElement) => {
                    // Filter out problematic elements
                    // Skip elements that might cause font issues
                    if (node.tagName === 'STYLE') return false;
                    if (node.tagName === 'LINK' && node.getAttribute('rel') === 'stylesheet') return false;
                    return true;
                }
            });

            return dataUrl;
        } catch (error) {
            console.error('Error capturing planning table:', error);
            // Don't fail the whole export if capture fails
            return null;
        } finally {
            // Always restore original styles
            element.style.overflow = originalOverflow;
            element.style.maxHeight = originalMaxHeight;
            element.style.height = originalHeight;
            element.style.width = originalWidth;
            element.style.position = originalPosition;
        }
    };

    /**
     * Handle annual planning PDF export
     */
    const handleExportAnnualPDF = async () => {
        if (!selectedSeasonId || !selectedTeamId) {
            alert(t("Please select a season and team first."));
            return;
        }

        // Check if planning has data
        if (planning.months.length === 0) {
            alert(t("No planning data available. Please load planning data first."));
            return;
        }

        try {
            setExportingAnnualPDF(true);

            // Capture planning table as image
            const tableImage = await capturePlanningTable();

            // Call the API to get the PDF
            const response = await exportAnnualPlanningPDF(
                selectedSeasonId,
                selectedTeamId,
                tableImage
            );

            if (response.data.success) {
                // Decode base64 and create blob
                const binaryString = atob(response.data.content);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                const blob = new Blob([bytes], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);

                // Download the file
                const link = document.createElement('a');
                link.href = url;
                link.download = response.data.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else {
                alert(t("Error generating PDF. Try again later."));
            }
        } catch (error) {
            console.error('Error exporting annual PDF:', error);
            alert(t("Error generating PDF. Try again later."));
        } finally {
            setExportingAnnualPDF(false);
        }
    };

    // Macrocycle drag selection handlers
    const handleMacrocycleCellMouseDown = (weekKey: string) => {
        // Check if the week is already part of a macrocycle
        const existingMacrocycle = macrocycles.find(m => m.weeks.includes(weekKey));
        if (existingMacrocycle) {
            // Don't allow selection if already part of a macrocycle
            return;
        }

        setIsDragging(true);
        setSelectedWeeks([weekKey]);
    }

    const handleMacrocycleCellMouseEnter = (weekKey: string) => {
        if (!isDragging) return;

        // Check if the week is already part of a macrocycle
        const existingMacrocycle = macrocycles.find(m => m.weeks.includes(weekKey));
        if (existingMacrocycle) {
            return;
        }

        // Only add if not already selected
        if (!selectedWeeks.includes(weekKey)) {
            setSelectedWeeks([...selectedWeeks, weekKey]);
        }
    }

    const handleMacrocycleCellMouseUp = () => {
        if (isDragging && selectedWeeks.length > 0) {
            setIsDragging(false);
            setShowMacrocycleModal(true);
        }
    }

    const handleSaveMacrocycle = async (name: string, color: string) => {
        let updatedMacrocycles: Macrocycle[];

        if (editingMacrocycle) {
            // Edit existing macrocycle
            updatedMacrocycles = macrocycles.map(m => {
                if (m.weeks.join(',') === editingMacrocycle.weeks.join(',')) {
                    return {
                        ...m,
                        name,
                        color
                    };
                }
                return m;
            });
        } else {
            // Create new macrocycle
            const newMacrocycle: Macrocycle = {
                name,
                color,
                weeks: selectedWeeks.sort() // Sort weeks to keep them in order
            };
            updatedMacrocycles = [...macrocycles, newMacrocycle];
        }

        setMacrocycles(updatedMacrocycles);

        // Save to backend
        const body = {
            season_id: selectedSeasonId,
            team_id: selectedTeamId,
            macrocycles: updatedMacrocycles,
            periods: periods,
            mesocycles: mesocycles
        };

        try {
            await updatePlanningPeriodicity(body);
            setShowMacrocycleModal(false);
            setSelectedWeeks([]);
            setEditingMacrocycle(null);
        } catch (error) {
            console.error('Error saving macrocycle:', error);
            alert(t("Error saving macrocycle. Please try again."));
        }
    }

    const handleCloseMacrocycleModal = () => {
        setShowMacrocycleModal(false);
        setSelectedWeeks([]);
        setIsDragging(false);
        setEditingMacrocycle(null);
    }

    const handleEditMacrocycle = (macrocycle: Macrocycle) => {
        setEditingMacrocycle(macrocycle);
        setShowMacrocycleModal(true);
    }

    const handleDeleteMacrocycle = async (macrocycle: Macrocycle) => {
        if (!window.confirm(t("Are you sure you want to delete this macrocycle?"))) {
            return;
        }

        const updatedMacrocycles = macrocycles.filter(m =>
            m.weeks.join(',') !== macrocycle.weeks.join(',')
        );
        setMacrocycles(updatedMacrocycles);

        // Save to backend
        const body = {
            season_id: selectedSeasonId,
            team_id: selectedTeamId,
            macrocycles: updatedMacrocycles,
            periods: periods,
            mesocycles: mesocycles
        };

        try {
            await updatePlanningPeriodicity(body);
        } catch (error) {
            console.error('Error deleting macrocycle:', error);
            alert(t("Error deleting macrocycle. Please try again."));
        }
    }

    // Period handlers
    const handlePeriodCellMouseDown = (weekKey: string) => {
        const existingPeriod = periods.find(p => p.weeks.includes(weekKey));
        if (existingPeriod) return;

        setIsDraggingPeriod(true);
        setSelectedWeeksPeriod([weekKey]);
    }

    const handlePeriodCellMouseEnter = (weekKey: string) => {
        if (!isDraggingPeriod) return;

        const existingPeriod = periods.find(p => p.weeks.includes(weekKey));
        if (existingPeriod) return;

        if (!selectedWeeksPeriod.includes(weekKey)) {
            setSelectedWeeksPeriod([...selectedWeeksPeriod, weekKey]);
        }
    }

    const handlePeriodCellMouseUp = () => {
        if (isDraggingPeriod && selectedWeeksPeriod.length > 0) {
            setIsDraggingPeriod(false);
            setShowPeriodModal(true);
        }
    }

    const handleSavePeriod = async (name: string, color: string) => {
        let updatedPeriods: Period[];

        if (editingPeriod) {
            // Edit existing period
            updatedPeriods = periods.map(p => {
                if (p.weeks.join(',') === editingPeriod.weeks.join(',')) {
                    return {
                        ...p,
                        name,
                        color
                    };
                }
                return p;
            });
        } else {
            // Create new period
            const newPeriod: Period = {
                name,
                color,
                weeks: selectedWeeksPeriod.sort()
            };
            updatedPeriods = [...periods, newPeriod];
        }

        setPeriods(updatedPeriods);

        // Save to backend
        const body = {
            season_id: selectedSeasonId,
            team_id: selectedTeamId,
            macrocycles: macrocycles,
            periods: updatedPeriods,
            mesocycles: mesocycles
        };

        try {
            await updatePlanningPeriodicity(body);
            setShowPeriodModal(false);
            setSelectedWeeksPeriod([]);
            setEditingPeriod(null);
        } catch (error) {
            console.error('Error saving period:', error);
            alert(t("Error saving period. Please try again."));
        }
    }

    const handleClosePeriodModal = () => {
        setShowPeriodModal(false);
        setSelectedWeeksPeriod([]);
        setIsDraggingPeriod(false);
        setEditingPeriod(null);
    }

    const handleEditPeriod = (period: Period) => {
        setEditingPeriod(period);
        setShowPeriodModal(true);
    }

    const handleDeletePeriod = async (period: Period) => {
        if (!window.confirm(t("Are you sure you want to delete this period?"))) {
            return;
        }

        const updatedPeriods = periods.filter(p =>
            p.weeks.join(',') !== period.weeks.join(',')
        );
        setPeriods(updatedPeriods);

        // Save to backend
        const body = {
            season_id: selectedSeasonId,
            team_id: selectedTeamId,
            macrocycles: macrocycles,
            periods: updatedPeriods,
            mesocycles: mesocycles
        };

        try {
            await updatePlanningPeriodicity(body);
        } catch (error) {
            console.error('Error deleting period:', error);
            alert(t("Error deleting period. Please try again."));
        }
    }

    // Mesocycle handlers
    const handleMesocycleCellMouseDown = (weekKey: string) => {
        const existingMesocycle = mesocycles.find(m => m.weeks.includes(weekKey));
        if (existingMesocycle) return;

        setIsDraggingMesocycle(true);
        setSelectedWeeksMesocycle([weekKey]);
    }

    const handleMesocycleCellMouseEnter = (weekKey: string) => {
        if (!isDraggingMesocycle) return;

        const existingMesocycle = mesocycles.find(m => m.weeks.includes(weekKey));
        if (existingMesocycle) return;

        if (!selectedWeeksMesocycle.includes(weekKey)) {
            setSelectedWeeksMesocycle([...selectedWeeksMesocycle, weekKey]);
        }
    }

    const handleMesocycleCellMouseUp = () => {
        if (isDraggingMesocycle && selectedWeeksMesocycle.length > 0) {
            setIsDraggingMesocycle(false);
            setShowMesocycleModal(true);
        }
    }

    const handleSaveMesocycle = async (name: string, color: string, primaryObjective: string, mesocycleObjectives: string) => {
        let updatedMesocycles: Mesocycle[];

        if (editingMesocycle) {
            // Edit existing mesocycle
            updatedMesocycles = mesocycles.map(m => {
                if (m.weeks.join(',') === editingMesocycle.weeks.join(',')) {
                    return {
                        ...m,
                        name,
                        color,
                        primary_objective: primaryObjective,
                        mesocycle_objectives: mesocycleObjectives
                    };
                }
                return m;
            });
        } else {
            // Create new mesocycle
            const newMesocycle: Mesocycle = {
                name,
                color,
                weeks: selectedWeeksMesocycle.sort(),
                primary_objective: primaryObjective,
                mesocycle_objectives: mesocycleObjectives
            };
            updatedMesocycles = [...mesocycles, newMesocycle];
        }

        setMesocycles(updatedMesocycles);

        // Save to backend
        const body = {
            season_id: selectedSeasonId,
            team_id: selectedTeamId,
            macrocycles: macrocycles,
            periods: periods,
            mesocycles: updatedMesocycles
        };

        try {
            await updatePlanningPeriodicity(body);
            setShowMesocycleModal(false);
            setSelectedWeeksMesocycle([]);
            setEditingMesocycle(null);
        } catch (error) {
            console.error('Error saving mesocycle:', error);
            alert(t("Error saving mesocycle. Please try again."));
        }
    }

    const handleCloseMesocycleModal = () => {
        setShowMesocycleModal(false);
        setSelectedWeeksMesocycle([]);
        setIsDraggingMesocycle(false);
        setEditingMesocycle(null);
    }

    const handleEditMesocycle = (mesocycle: Mesocycle) => {
        setEditingMesocycle(mesocycle);
        setShowMesocycleModal(true);
    }

    const handleDeleteMesocycle = async (mesocycle: Mesocycle) => {
        if (!window.confirm(t("Are you sure you want to delete this mesocycle?"))) {
            return;
        }

        const updatedMesocycles = mesocycles.filter(m =>
            m.weeks.join(',') !== mesocycle.weeks.join(',')
        );
        setMesocycles(updatedMesocycles);

        // Save to backend
        const body = {
            season_id: selectedSeasonId,
            team_id: selectedTeamId,
            macrocycles: macrocycles,
            periods: periods,
            mesocycles: updatedMesocycles
        };

        try {
            await updatePlanningPeriodicity(body);
        } catch (error) {
            console.error('Error deleting mesocycle:', error);
            alert(t("Error deleting mesocycle. Please try again."));
        }
    }

    /**
     * ========================================
     * RACE HANDLERS
     * ========================================
     * Functions for managing races/competitions in the planning timeline
     */

    /**
     * Opens the race management modal for a specific week
     * @param week - The planning week to manage races for
     */
    const handleRaceCellClick = (week: PlanningWeek) => {
        setSelectedRaceWeek(week);
        setShowRaceModal(true);
    };

    /**
     * Closes the race modal and clears the selected week
     */
    const handleCloseRaceModal = () => {
        setShowRaceModal(false);
        setSelectedRaceWeek(null);
    };

    /**
     * Updates the planning state with modified races from the modal
     * @param updatedRaces - The updated array of races for the selected week
     * @param newWeekId - Optional new week ID if the week was created by the backend
     */
    const handleSaveRaces = (updatedRaces: PlanningRace[], newWeekId?: number) => {
        // Update the races in the planning state
        const updatedPlanning = { ...planning };
        updatedPlanning.months = updatedPlanning.months.map((month: PlanningMonth) => ({
            ...month,
            weeks: month.weeks.map((week: PlanningWeek) => {
                // Match by key (always available) instead of id (may be undefined for uninitialized weeks)
                if (week.key === selectedRaceWeek?.key) {
                    return {
                        ...week,
                        races: updatedRaces,
                        // Update the week id if it was created by the backend
                        id: newWeekId || week.id
                    };
                }
                return week;
            })
        }));
        setPlanning(updatedPlanning);

        // Also update selectedRaceWeek with the new id if provided
        if (newWeekId && selectedRaceWeek) {
            setSelectedRaceWeek({ ...selectedRaceWeek, id: newWeekId, races: updatedRaces });
        }
    };

    /**
     * ========================================
     * CONTENT DISTRIBUTION HANDLERS
     * ========================================
     * Functions for managing content distribution cells
     */

    /**
     * Gets the content value for a specific week and field
     */
    const getContentValue = (week: PlanningWeek, fieldKey: string): PlanningWeekContent | null => {
        if (!week.content) return null;
        return week.content.find(c => c.field === fieldKey) || null;
    };

    /**
     * Opens the content edit modal for a specific week and field
     */
    const handleContentCellClick = (week: PlanningWeek, fieldKey: string) => {
        const field = planningLevels.find(pl => pl.key === fieldKey);
        setSelectedContentWeek(week);
        setSelectedContentField(fieldKey);
        setSelectedContentFieldName(field?.name || fieldKey);
        setShowContentModal(true);
    };

    /**
     * Closes the content modal
     */
    const handleCloseContentModal = () => {
        setShowContentModal(false);
        setSelectedContentWeek(null);
        setSelectedContentField('');
        setSelectedContentFieldName('');
    };

    /**
     * Saves the content from the modal
     */
    const handleSaveContent = async (value: string, color: string, observations: string) => {
        if (!selectedContentWeek || !selectedContentWeek.id) return;

        const existingContent = getContentValue(selectedContentWeek, selectedContentField);

        try {
            let newContentId = 0;

            if (existingContent && existingContent.id) {
                // Update existing content
                const body = {
                    id: existingContent.id,
                    planning_week_id: selectedContentWeek.id,
                    field: selectedContentField,
                    value: value,
                    color: color,
                    observations: observations
                };
                await updatePlanningWeekContent(body);
                newContentId = existingContent.id;
            } else {
                // Create new content
                const body = {
                    planning_week_id: selectedContentWeek.id,
                    field: selectedContentField,
                    value: value,
                    color: color,
                    observations: observations
                };
                const response = await createPlanningWeekContent(body);
                // Get the ID from response if available
                if (response.data && response.data.id) {
                    newContentId = response.data.id;
                }
            }

            // Update local state
            const updatedPlanning = { ...planning };
            updatedPlanning.months = updatedPlanning.months.map((month: PlanningMonth) => ({
                ...month,
                weeks: month.weeks.map((w: PlanningWeek) => {
                    if (w.key !== selectedContentWeek.key) return w;

                    const updatedContent = [...(w.content || [])];
                    const contentIndex = updatedContent.findIndex(c => c.field === selectedContentField);

                    if (contentIndex >= 0) {
                        updatedContent[contentIndex] = {
                            ...updatedContent[contentIndex],
                            value: value,
                            color: color,
                            observations: observations
                        };
                    } else {
                        updatedContent.push({
                            id: newContentId,
                            planning_week_id: selectedContentWeek.id!,
                            field: selectedContentField,
                            value: value,
                            color: color,
                            observations: observations
                        });
                    }

                    return { ...w, content: updatedContent };
                })
            }));

            setPlanning(updatedPlanning);
        } catch (error) {
            console.error('Error saving content:', error);
            alert(t("Error saving content. Please try again."));
        }
    };

    /**
     * ========================================
     * TOTAL SESSIONS HANDLERS
     * ========================================
     * Handles modal for editing total_sessions
     */

    /**
     * Opens the total sessions modal for a specific week
     */
    const handleTotalSessionsClick = (week: PlanningWeek) => {
        setSelectedSessionsWeek(week);
        setShowTotalSessionsModal(true);
    };

    /**
     * Closes the total sessions modal
     */
    const handleCloseTotalSessionsModal = () => {
        setShowTotalSessionsModal(false);
        setSelectedSessionsWeek(null);
    };

    /**
     * Saves the total sessions value
     */
    const handleSaveTotalSessions = async (value: number) => {
        if (!selectedSessionsWeek) return;

        try {
            const body: any = {
                season_id: Number(selectedSeasonId),
                team_id: Number(selectedTeamId),
                week: selectedSessionsWeek.key,
                workload: selectedSessionsWeek.workload || 0,
                total_sessions: value
            };

            await updatePlanningWeek(body);

            // Update local state
            const updatedPlanning = { ...planning };
            updatedPlanning.months = updatedPlanning.months.map((month: PlanningMonth) => ({
                ...month,
                weeks: month.weeks.map((w: PlanningWeek) => {
                    if (w.key === selectedSessionsWeek.key) {
                        return { ...w, total_sessions: value };
                    }
                    return w;
                })
            }));

            setPlanning(updatedPlanning);
        } catch (error) {
            console.error('Error saving total_sessions:', error);
            alert(t("Error saving. Please try again."));
        }
    };

    /**
     * ========================================
     * COPY/PASTE WEEK HANDLERS
     * ========================================
     * Handles context menu and copy/paste operations for weeks
     */

    /**
     * Opens context menu on right-click for a week cell
     */
    const handleWeekContextMenu = (event: React.MouseEvent, monthKey: string, weekKey: string) => {
        event.preventDefault();
        setContextMenuPosition({ x: event.clientX, y: event.clientY });
        setContextMenuWeek({ month: monthKey, week: weekKey });
        setContextMenuVisible(true);
    };

    /**
     * Copies a week's reference (just stores the identifiers, doesn't copy data yet)
     */
    const handleCopyWeek = () => {
        if (!contextMenuWeek) return;

        // Just store the reference to the week
        setCopiedWeekRef({
            season_id: Number(selectedSeasonId),
            team_id: Number(selectedTeamId),
            week_key: contextMenuWeek.week
        });

        setContextMenuVisible(false);
    };

    /**
     * Pastes copied week data into the selected week
     * Calls the backend endpoint with source and destination week references
     * The backend handles deleting destination content and copying source content
     */
    const handlePasteWeek = async () => {
        if (!contextMenuWeek || !copiedWeekRef) return;

        const monthIndex = planning.months.findIndex((m: PlanningMonth) => m.key === contextMenuWeek.month);
        if (monthIndex === -1) return;

        const weekIndex = planning.months[monthIndex].weeks.findIndex((w: PlanningWeek) => w.key === contextMenuWeek.week);
        if (weekIndex === -1) return;

        const targetWeek = planning.months[monthIndex].weeks[weekIndex];

        try {
            // Call the backend paste endpoint with source and destination week references.
            // Source season/team come from the copied reference; destination from current selection,
            // so paste works across teams (and even across seasons).
            const pasteBody = {
                source_season_id: copiedWeekRef.season_id,
                source_team_id: copiedWeekRef.team_id,
                source_week: copiedWeekRef.week_key,
                destination_season_id: Number(selectedSeasonId),
                destination_team_id: Number(selectedTeamId),
                destination_week: targetWeek.key
            };

            const response = await pastePlanningWeek(pasteBody);

            // Check response status
            if (response.data.status === -1) {
                alert(t("Error") + ": " + response.data.message);
                setContextMenuVisible(false);
                return;
            }

            // Success - reload the planning data in place (no spinner unmount so scroll/focus stays)
            setContextMenuVisible(false);

            getPlanning(selectedSeasonId, selectedTeamId).then((planningResponse: any) => {
                const translatedPlanning = translatePlanningMonths(planningResponse.data);
                setPlanning(translatedPlanning);

                // Reload macrocycles, periods, and mesocycles
                const macrocyclesData = planningResponse.data.macrocycles || [];
                const periodsData = planningResponse.data.periods || [];
                const mesocyclesData = planningResponse.data.mesocycles || [];

                const macrocyclesArray = (Array.isArray(macrocyclesData) ? macrocyclesData : Object.values(macrocyclesData)).map((mc: any) => {
                    const weeksArray = Array.isArray(mc.weeks) ? mc.weeks : (mc.weeks ? Object.values(mc.weeks) : []);
                    return {
                        id: mc.id,
                        name: mc.name || '',
                        color: mc.color || '#000000',
                        weeks: weeksArray
                    };
                });

                const periodsArray = (Array.isArray(periodsData) ? periodsData : Object.values(periodsData)).map((p: any) => {
                    const weeksArray = Array.isArray(p.weeks) ? p.weeks : (p.weeks ? Object.values(p.weeks) : []);
                    return {
                        id: p.id,
                        name: p.name || '',
                        color: p.color || '#000000',
                        weeks: weeksArray
                    };
                });

                const mesocyclesArray = (Array.isArray(mesocyclesData) ? mesocyclesData : Object.values(mesocyclesData)).map((m: any) => {
                    const weeksArray = Array.isArray(m.weeks) ? m.weeks : (m.weeks ? Object.values(m.weeks) : []);
                    return {
                        id: m.id,
                        name: m.name || '',
                        color: m.color || '#000000',
                        weeks: weeksArray,
                        primary_objective: m.primary_objective || '',
                        mesocycle_objectives: m.mesocycle_objectives || ''
                    };
                });

                setMacrocycles(macrocyclesArray);
                setPeriods(periodsArray);
                setMesocycles(mesocyclesArray);
            }).catch((error: any) => {
                console.error('Error reloading planning:', error);
                alert(t("Error reloading planning. Please refresh the page."));
            });
        } catch (error) {
            console.error('Error pasting week:', error);
            alert(t("Error pasting week. Please try again."));
            setContextMenuVisible(false);
        }
    };

    /**
     * Opens the weekly planning view in a modal overlay
     */
    const handleOpenWeeklyView = () => {
        if (!contextMenuWeek) return;

        const monthIndex = planning.months.findIndex((m: PlanningMonth) => m.key === contextMenuWeek.month);
        if (monthIndex === -1) return;

        const weekIndex = planning.months[monthIndex].weeks.findIndex((w: PlanningWeek) => w.key === contextMenuWeek.week);
        if (weekIndex === -1) return;

        const targetWeek = planning.months[monthIndex].weeks[weekIndex];

        // Set modal parameters and show modal
        setWeeklyModalParams({
            seasonId: selectedSeasonId,
            teamId: selectedTeamId,
            weekKey: targetWeek.key
        });
        setShowWeeklyModal(true);
        setContextMenuVisible(false);
    };

    /**
     * Closes the weekly planning modal
     */
    const handleCloseWeeklyModal = () => {
        setShowWeeklyModal(false);
        setWeeklyModalParams(null);
    };

    /**
     * Clears all data from a week (irreversible operation)
     * Asks for user confirmation before proceeding
     */
    const handleClearWeek = async () => {
        if (!contextMenuWeek) return;

        const monthIndex = planning.months.findIndex((m: PlanningMonth) => m.key === contextMenuWeek.month);
        if (monthIndex === -1) return;

        const weekIndex = planning.months[monthIndex].weeks.findIndex((w: PlanningWeek) => w.key === contextMenuWeek.week);
        if (weekIndex === -1) return;

        const targetWeek = planning.months[monthIndex].weeks[weekIndex];

        // Ask for confirmation (this is irreversible)
        const confirmMessage = t("Are you sure you want to clear all data from week {{week}}? This action cannot be undone.", { week: targetWeek.name });
        if (!window.confirm(confirmMessage)) {
            setContextMenuVisible(false);
            return;
        }

        try {
            // Call the backend clear endpoint
            const clearBody = {
                season_id: Number(selectedSeasonId),
                team_id: Number(selectedTeamId),
                week: targetWeek.key
            };

            const response = await clearPlanningWeek(clearBody);

            // Check response status
            if (response.data.status === -1) {
                alert(t("Error") + ": " + response.data.message);
                setContextMenuVisible(false);
                return;
            }

            // Success - reload the planning data in place (no spinner unmount so scroll/focus stays)
            setContextMenuVisible(false);

            getPlanning(selectedSeasonId, selectedTeamId).then((planningResponse: any) => {
                const translatedPlanning = translatePlanningMonths(planningResponse.data);
                setPlanning(translatedPlanning);

                // Reload macrocycles, periods, and mesocycles
                const macrocyclesData = planningResponse.data.macrocycles || [];
                const periodsData = planningResponse.data.periods || [];
                const mesocyclesData = planningResponse.data.mesocycles || [];

                const macrocyclesArray = (Array.isArray(macrocyclesData) ? macrocyclesData : Object.values(macrocyclesData)).map((mc: any) => {
                    const weeksArray = Array.isArray(mc.weeks) ? mc.weeks : (mc.weeks ? Object.values(mc.weeks) : []);
                    return {
                        id: mc.id,
                        name: mc.name || '',
                        color: mc.color || '#000000',
                        weeks: weeksArray
                    };
                });

                const periodsArray = (Array.isArray(periodsData) ? periodsData : Object.values(periodsData)).map((p: any) => {
                    const weeksArray = Array.isArray(p.weeks) ? p.weeks : (p.weeks ? Object.values(p.weeks) : []);
                    return {
                        id: p.id,
                        name: p.name || '',
                        color: p.color || '#000000',
                        weeks: weeksArray
                    };
                });

                const mesocyclesArray = (Array.isArray(mesocyclesData) ? mesocyclesData : Object.values(mesocyclesData)).map((m: any) => {
                    const weeksArray = Array.isArray(m.weeks) ? m.weeks : (m.weeks ? Object.values(m.weeks) : []);
                    return {
                        id: m.id,
                        name: m.name || '',
                        color: m.color || '#000000',
                        weeks: weeksArray,
                        primary_objective: m.primary_objective || '',
                        mesocycle_objectives: m.mesocycle_objectives || ''
                    };
                });

                setMacrocycles(macrocyclesArray);
                setPeriods(periodsArray);
                setMesocycles(mesocyclesArray);
            }).catch((error: any) => {
                console.error('Error reloading planning:', error);
                alert(t("Error reloading planning. Please refresh the page."));
            });
        } catch (error) {
            console.error('Error clearing week:', error);
            alert(t("Error clearing week. Please try again."));
            setContextMenuVisible(false);
        }
    };

    // Helper function to render macrocycle cells with colSpan
    const renderMacrocycleCells = () => {
        const cells: React.ReactElement[] = [];

        planning.months.forEach((m: PlanningMonth) => {
            m.weeks.forEach((w: PlanningWeek) => {
                // Check if this week is part of a macrocycle
                const macrocycle = macrocycles.find(mc => mc.weeks.includes(w.key));

                if (macrocycle) {
                    // Check if this is the first week of the macrocycle
                    const firstWeekOfMacrocycle = macrocycle.weeks[0] === w.key;

                    if (firstWeekOfMacrocycle) {
                        const macrocycleKey = macrocycle.weeks.join(',');
                        const isHovered = hoveredMacrocycle === macrocycleKey;

                        // Render a cell with colSpan equal to the number of weeks
                        cells.push(
                            <td
                                key={'macrocycle-' + w.key}
                                colSpan={macrocycle.weeks.length}
                                className="macrocycle-week-value macrocycle-defined"
                                style={{
                                    backgroundColor: macrocycle.color,
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                    verticalAlign: 'middle',
                                    position: 'relative'
                                }}
                                onMouseEnter={() => setHoveredMacrocycle(macrocycleKey)}
                                onMouseLeave={() => setHoveredMacrocycle(null)}
                            >
                                <span>{macrocycle.name}</span>
                                {isHovered && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: '10px',
                                        transform: 'translateY(-50%)',
                                        display: 'flex',
                                        gap: '8px',
                                        backgroundColor: 'transparent',
                                        padding: '4px'
                                    }}>
                                        <i
                                            className="bi bi-pencil-square"
                                            style={{
                                                cursor: 'pointer',
                                                color: '#000',
                                                fontSize: '18px'
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditMacrocycle(macrocycle);
                                            }}
                                        />
                                        <i
                                            className="bi bi-trash"
                                            style={{
                                                cursor: 'pointer',
                                                color: '#dc3545',
                                                fontSize: '18px'
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteMacrocycle(macrocycle);
                                            }}
                                        />
                                    </div>
                                )}
                            </td>
                        );
                    }
                    // If not the first week, skip (already rendered with colSpan)
                } else {
                    // Empty cell - allow drag selection
                    const isSelected = selectedWeeks.includes(w.key);
                    cells.push(
                        <td
                            key={'macrocycle-week-value-' + w.key}
                            className={`macrocycle-week-value ${isSelected ? 'selected' : ''}`}
                            onMouseDown={() => handleMacrocycleCellMouseDown(w.key)}
                            onMouseEnter={() => handleMacrocycleCellMouseEnter(w.key)}
                            onMouseUp={handleMacrocycleCellMouseUp}
                            style={{
                                cursor: 'cell',
                                userSelect: 'none',
                                backgroundColor: isSelected ? '#e0e0e0' : 'transparent',
                                border: isSelected ? '2px solid #999' : undefined
                            }}
                        >
                            &nbsp;
                        </td>
                    );
                }
            });
        });

        return cells;
    }

    // Helper function to render period cells with colSpan
    const renderPeriodCells = () => {
        const cells: React.ReactElement[] = [];

        planning.months.forEach((m: PlanningMonth) => {
            m.weeks.forEach((w: PlanningWeek) => {
                // Check if this week is part of a period
                const period = periods.find(p => p.weeks.includes(w.key));

                if (period) {
                    // Check if this is the first week of the period
                    const firstWeekOfPeriod = period.weeks[0] === w.key;

                    if (firstWeekOfPeriod) {
                        const periodKey = period.weeks.join(',');
                        const isHovered = hoveredPeriod === periodKey;

                        // Render a cell with colSpan equal to the number of weeks
                        cells.push(
                            <td
                                key={'period-' + w.key}
                                colSpan={period.weeks.length}
                                className="period-week-value period-defined"
                                style={{
                                    backgroundColor: period.color,
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                    verticalAlign: 'middle',
                                    position: 'relative'
                                }}
                                onMouseEnter={() => setHoveredPeriod(periodKey)}
                                onMouseLeave={() => setHoveredPeriod(null)}
                            >
                                <span>{period.name}</span>
                                {isHovered && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: '10px',
                                        transform: 'translateY(-50%)',
                                        display: 'flex',
                                        gap: '8px',
                                        backgroundColor: 'transparent',
                                        padding: '4px'
                                    }}>
                                        <i
                                            className="bi bi-pencil-square"
                                            style={{
                                                cursor: 'pointer',
                                                color: '#000',
                                                fontSize: '18px'
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditPeriod(period);
                                            }}
                                        />
                                        <i
                                            className="bi bi-trash"
                                            style={{
                                                cursor: 'pointer',
                                                color: '#dc3545',
                                                fontSize: '18px'
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeletePeriod(period);
                                            }}
                                        />
                                    </div>
                                )}
                            </td>
                        );
                    }
                    // If not the first week, skip (already rendered with colSpan)
                } else {
                    // Empty cell - allow drag selection
                    const isSelected = selectedWeeksPeriod.includes(w.key);
                    cells.push(
                        <td
                            key={'period-week-value-' + w.key}
                            className={`period-week-value ${isSelected ? 'selected' : ''}`}
                            onMouseDown={() => handlePeriodCellMouseDown(w.key)}
                            onMouseEnter={() => handlePeriodCellMouseEnter(w.key)}
                            onMouseUp={handlePeriodCellMouseUp}
                            style={{
                                cursor: 'cell',
                                userSelect: 'none',
                                backgroundColor: isSelected ? '#e0e0e0' : 'transparent',
                                border: isSelected ? '2px solid #999' : undefined
                            }}
                        >
                            &nbsp;
                        </td>
                    );
                }
            });
        });

        return cells;
    }

    // Helper function to render mesocycle cells with colSpan
    const renderMesocycleCells = () => {
        const cells: React.ReactElement[] = [];

        planning.months.forEach((m: PlanningMonth) => {
            m.weeks.forEach((w: PlanningWeek) => {
                const mesocycle = mesocycles.find(mc => mc.weeks.includes(w.key));

                if (mesocycle) {
                    const firstWeekOfMesocycle = mesocycle.weeks[0] === w.key;

                    if (firstWeekOfMesocycle) {
                        const mesocycleKey = mesocycle.weeks.join(',');
                        const isHovered = hoveredMesocycle === mesocycleKey;

                        cells.push(
                            <td
                                key={'mesocycle-' + w.key}
                                colSpan={mesocycle.weeks.length}
                                className="mesocycle-week-value mesocycle-defined"
                                style={{
                                    backgroundColor: mesocycle.color || '#ffffcc',
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                    verticalAlign: 'middle',
                                    position: 'relative'
                                }}
                                onMouseEnter={() => setHoveredMesocycle(mesocycleKey)}
                                onMouseLeave={() => setHoveredMesocycle(null)}
                            >
                                <span>{mesocycle.name}</span>
                                {isHovered && (
                                    <div style={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: '10px',
                                        transform: 'translateY(-50%)',
                                        display: 'flex',
                                        gap: '8px',
                                        backgroundColor: 'transparent',
                                        padding: '4px'
                                    }}>
                                        <i
                                            className="bi bi-pencil-square"
                                            style={{
                                                cursor: 'pointer',
                                                color: '#000',
                                                fontSize: '18px'
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditMesocycle(mesocycle);
                                            }}
                                        />
                                        <i
                                            className="bi bi-trash"
                                            style={{
                                                cursor: 'pointer',
                                                color: '#dc3545',
                                                fontSize: '18px'
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteMesocycle(mesocycle);
                                            }}
                                        />
                                    </div>
                                )}
                            </td>
                        );
                    }
                } else {
                    const isSelected = selectedWeeksMesocycle.includes(w.key);
                    cells.push(
                        <td
                            key={'mesocycle-week-value-' + w.key}
                            className={`mesocycle-week-value ${isSelected ? 'selected' : ''}`}
                            onMouseDown={() => handleMesocycleCellMouseDown(w.key)}
                            onMouseEnter={() => handleMesocycleCellMouseEnter(w.key)}
                            onMouseUp={handleMesocycleCellMouseUp}
                            style={{
                                cursor: 'cell',
                                userSelect: 'none',
                                backgroundColor: isSelected ? '#e0e0e0' : 'transparent',
                                border: isSelected ? '2px solid #999' : undefined
                            }}
                        >
                            &nbsp;
                        </td>
                    );
                }
            });
        });

        return cells;
    }

    // Helper function to render primary objective cells (shares mesocycle colSpan)
    const renderPrimaryObjectiveCells = () => {
        const cells: React.ReactElement[] = [];

        planning.months.forEach((m: PlanningMonth) => {
            m.weeks.forEach((w: PlanningWeek) => {
                const mesocycle = mesocycles.find(mc => mc.weeks.includes(w.key));

                if (mesocycle) {
                    const firstWeekOfMesocycle = mesocycle.weeks[0] === w.key;

                    if (firstWeekOfMesocycle) {
                        cells.push(
                            <td
                                key={'primary-objective-' + w.key}
                                colSpan={mesocycle.weeks.length}
                                className="main-objective-week-value"
                                style={{
                                    backgroundColor: mesocycle.color || '#ffffcc',
                                    textAlign: 'center',
                                    verticalAlign: 'middle'
                                }}
                            >
                                {mesocycle.primary_objective || ''}
                            </td>
                        );
                    }
                } else {
                    cells.push(
                        <td
                            key={'primary-objective-week-value-' + w.key}
                            className="main-objective-week-value"
                        >
                            &nbsp;
                        </td>
                    );
                }
            });
        });

        return cells;
    }

    // Helper function to render mesocycle objectives cells (shares mesocycle colSpan)
    const renderMesocycleObjectivesCells = () => {
        const cells: React.ReactElement[] = [];

        planning.months.forEach((m: PlanningMonth) => {
            m.weeks.forEach((w: PlanningWeek) => {
                const mesocycle = mesocycles.find(mc => mc.weeks.includes(w.key));

                if (mesocycle) {
                    const firstWeekOfMesocycle = mesocycle.weeks[0] === w.key;

                    if (firstWeekOfMesocycle) {
                        cells.push(
                            <td
                                key={'mesocycle-objectives-' + w.key}
                                colSpan={mesocycle.weeks.length}
                                className="mesocycle-objectives-week-value"
                                style={{
                                    backgroundColor: mesocycle.color || '#ffffcc',
                                    textAlign: 'center',
                                    verticalAlign: 'middle'
                                }}
                            >
                                {mesocycle.mesocycle_objectives || ''}
                            </td>
                        );
                    }
                } else {
                    cells.push(
                        <td
                            key={'mesocycle-objectives-week-value-' + w.key}
                            className="mesocycle-objectives-week-value"
                        >
                            &nbsp;
                        </td>
                    );
                }
            });
        });

        return cells;
    }

    /**
     * Renders content distribution cells for a specific field
     * Each cell shows the value and can be clicked to open the edit modal
     * Displays a custom tooltip on hover with value, color, and observations
     * @param fieldKey - The field key to render cells for
     * @returns Array of table cells (td) with content information
     */
    const renderContentDistributionCells = (fieldKey: string) => {
        const cells: React.ReactElement[] = [];

        planning.months.forEach((m: PlanningMonth) => {
            m.weeks.forEach((w: PlanningWeek) => {
                const contentValue = getContentValue(w, fieldKey);
                const hasContent = contentValue && (contentValue.value || contentValue.observations);

                cells.push(
                    <td
                        key={`content-${fieldKey}-${w.key}`}
                        className="content-distribution-cell"
                        style={{
                            backgroundColor: contentValue?.color || '#ffffff',
                            cursor: 'pointer'
                        }}
                        onClick={() => handleContentCellClick(w, fieldKey)}
                    >
                        {contentValue?.value || ''}
                        {/* Custom tooltip showing content details */}
                        {hasContent && (
                            <div className="content-tooltip">
                                {contentValue.value && (
                                    <div className="content-tooltip-value">
                                        {t("Value")}: {contentValue.value}
                                    </div>
                                )}
                                {contentValue.observations && (
                                    <div className="content-tooltip-observations">
                                        {contentValue.observations}
                                    </div>
                                )}
                            </div>
                        )}
                    </td>
                );
            });
        });

        return cells;
    };

    /**
     * Renders the race cells in the Timeline section of the planning table
     * Each cell shows:
     * - Race count (or empty if no races)
     * - Custom tooltip on hover with race titles and descriptions
     * - Click to open race management modal
     *
     * @returns Array of table header cells (th) with race information
     */
    const renderRaceCells = () => {
        const cells: React.ReactElement[] = [];

        planning.months.forEach((m: PlanningMonth) => {
            m.weeks.forEach((w: PlanningWeek) => {
                const raceCount = w.races?.length || 0;
                const hasRaces = raceCount > 0;

                cells.push(
                    <th
                        key={'race-week-value-' + w.key}
                        className="race-week-value"
                        onClick={() => handleRaceCellClick(w)}
                    >
                        {raceCount > 0 ? raceCount : ''}
                        {/* Custom tooltip showing race details */}
                        {hasRaces && (
                            <div className="race-tooltip">
                                {w.races?.map((race, index) => (
                                    <div key={race.id || index} className="race-tooltip-item">
                                        <div className="race-tooltip-title">{race.title}</div>
                                        {race.description && (
                                            <div className="race-tooltip-description">{race.description}</div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </th>
                );
            });
        });

        return cells;
    }

    return (
        <div className="planning-container">
            <h1>
                { t('Planning')}
            </h1>
            <div className="filters-container">
                { !loaded && 
                    <LoadingSpinner />
                }
                { loaded &&
                    <Form className="form-planning-filter" onSubmit={ handleSearchSubmit }>
                        <Form.Group className="field-season" controlId="season">
                            <Form.Select aria-label={ t("season") } name="season_id" required onChange={ handleSeasonChange } value={ selectedSeasonId || '' }>
                                <option value="">{ t("select_season") }</option>
                                { seasonOptions.map((s: Season) => {
                                    return <option key={ 'season-' + s.id }
                                        value={ s.id }>{ s.name }</option>
                                })}
                            </Form.Select>
                        </Form.Group>
                        { selectedSeasonId != 0 &&
                            <Form.Group className="field-club" controlId="club">
                                <Form.Select aria-label={ t("club") } name="club_id" required onChange={ handleClubChange }>
                                    <option value="">{ t("select_club") }</option>
                                    { clubOptions.map((t: Club) => {
                                        return <option key={ 'club-' + t.id }
                                            value={ t.id }>{ t.name }</option>
                                    })}
                                </Form.Select>
                            </Form.Group>
                        }
                        { selectedClubId != 0 &&
                            <Form.Group className="field-team" controlId="team">
                                <Form.Select aria-label={ t("team") } name="team_id" required onChange={ handleTeamChange }>
                                    <option value="">{ t("select_team") }</option>
                                    { teamOptions.map((t: Team) => {
                                        return <option key={ 'team-' + t.id }
                                            value={ t.id }>{ t.name }</option>
                                    })}
                                </Form.Select>
                            </Form.Group>
                        }
                        { selectedSeasonId != 0 && selectedClubId != 0 && selectedTeamId != 0 &&
                            <div className="actions">
                                <Button variant="primary" type="submit">
                                    { t("search") }
                                </Button>
                                { planning.months.length > 0 && !refreshing &&
                                    <Button
                                        variant="success"
                                        onClick={handleExportAnnualPDF}
                                        disabled={exportingAnnualPDF}
                                        className="ms-auto"
                                    >
                                        {exportingAnnualPDF ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                {t("Generating...")}
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-file-pdf me-2"></i>
                                                {t("Export Annual PDF")}
                                            </>
                                        )}
                                    </Button>
                                }
                            </div>
                        }
                    </Form>
                }
            </div>
            { loaded && refreshing && <LoadingSpinner /> }
            { loaded && !refreshing && planning.months.length > 0 &&
                <>
                <div ref={topScrollRef} className="season-planning-top-scrollbar" onScroll={handleTopScroll}>
                    <div ref={topScrollInnerRef} style={{ height: '12px' }} />
                </div>
                <div ref={planningTableRef} className="season-planning-table-container" onScroll={handleTableScroll}>
                    <Table className="season-planning-table">
                        <thead>
                            <tr>
                                <th className="heading-timeline sticky1" rowSpan={ 5 } align="center">
                                    <div className="timeline-label">
                                        { t("Timeline") }
                                    </div>
                                </th>
                                <th colSpan={2} className="heading-month sticky2">{ t("Month") }</th>
                                { planning.months.map((m: PlanningMonth) => (
                                        <th colSpan={ m.weeks.length } key={ 'heading-month-column-' + m.key } className={ 'heading-month-item ' + 'heading-planning-num-days-' + m.weeks.length } data-key={ m.key }>
                                                { m.name }
                                            </th>
                                ))}
                            </tr>
                            <tr>
                                <th colSpan={2} className="sticky2">{ t("Week") }</th>
                                {
                                    planning.months.map((m: PlanningMonth) => (
                                        m.weeks.map((w: PlanningWeek) => (
                                            <th
                                                key={ 'heading-week-column-' + w.key }
                                                className="heading-week-column"
                                                onContextMenu={(e) => handleWeekContextMenu(e, m.key, w.key)}
                                            >
                                                <button onClick={ handleShowWeekPlanner } data-month={ m.key } data-week={ w.key }>{ w.name }</button>
                                            </th>
                                        ))
                                    ))
                                }
                            </tr>
                            <tr>
                                <th colSpan={2} className="heading-month-day-mon-label sticky2">{ t("Day (Mon)") }</th>
                                {
                                    planning.months.map((m: PlanningMonth) => (
                                        m.weeks.map((w: PlanningWeek) => (
                                            <th className="heading-month-day-mon sticky" key={ 'heading-week-from-column-' + w.key }>{ w.from_name }</th>
                                        ))
                                    ))
                                }
                            </tr>
                            <tr>
                                <th colSpan={2} className="heading-month-day-sun-label sticky2">{ t("Day (Sun)") }</th>
                                {
                                    planning.months.map((m: PlanningMonth) => (
                                        m.weeks.map((w: PlanningWeek) => (
                                            <th className="heading-month-day-sun sticky" key={ 'heading-week-to-column-' + w.key }>{ w.to_name }</th>
                                        ))
                                    ))
                                }
                            </tr>
                            <tr>
                                <th colSpan={2} className="heading-races-label sticky2">{ t("Races") }</th>
                                {renderRaceCells()}
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td rowSpan={ 8 } className="workload-label sticky1">
                                        <div>{ t("Workload")}</div>
                                </td>
                                <td colSpan={2} className="workload-pct-label sticky2">{ '100%' }</td>
                                {
                                    planning.months.map((m: PlanningMonth) => (
                                        m.weeks.map((w: PlanningWeek) => {
                                            const mesocycle = mesocycles.find(mc => mc.weeks.includes(w.key));
                                            const isColored = 100 <= w.workload;
                                            const backgroundColor = (isColored && mesocycle) ? mesocycle.color : undefined;
                                            return (
                                                <td key={ 'workload-pct-value-100-' + w.key }
                                                    className={ 'workload-pct workload-pct-100 workload-value-' + w.workload }
                                                    style={{ backgroundColor }}
                                                    data-team={ selectedTeamId } data-season={ selectedSeasonId }
                                                    data-week={ w.key } data-workload={ 100 } onClick={ handleWorkloadChange }>
                                                    &nbsp;
                                                </td>
                                            );
                                        })
                                    ))
                                }
                            </tr>
                            <tr>
                                <td colSpan={2} className="workload-pct-label sticky2">{ '90%' }</td>
                                {
                                    planning.months.map((m: PlanningMonth) => (
                                        m.weeks.map((w: PlanningWeek) => {
                                            const mesocycle = mesocycles.find(mc => mc.weeks.includes(w.key));
                                            const isColored = 90 <= w.workload;
                                            const backgroundColor = (isColored && mesocycle) ? mesocycle.color : undefined;
                                            return (
                                                <td key={ 'workload-pct-value-90-' + w.key }
                                                    className={ 'workload-pct workload-pct-90 workload-value-' + w.workload }
                                                    style={{ backgroundColor }}
                                                    data-team={ selectedTeamId } data-season={ selectedSeasonId }
                                                    data-week={ w.key } data-workload={ 90 } onClick={ handleWorkloadChange }>
                                                    &nbsp;
                                                </td>
                                            );
                                        })
                                    ))
                                }
                            </tr>
                            <tr>
                                <td colSpan={2} className="workload-pct-label sticky2">{ '80%' }</td>
                                {
                                    planning.months.map((m: PlanningMonth) => (
                                        m.weeks.map((w: PlanningWeek) => {
                                            const mesocycle = mesocycles.find(mc => mc.weeks.includes(w.key));
                                            const isColored = 80 <= w.workload;
                                            const backgroundColor = (isColored && mesocycle) ? mesocycle.color : undefined;
                                            return (
                                                <td key={ 'workload-pct-value-80-' + w.key }
                                                    className={ 'workload-pct workload-pct-80 workload-value-' + w.workload }
                                                    style={{ backgroundColor }}
                                                    data-team={ selectedTeamId } data-season={ selectedSeasonId }
                                                    data-week={ w.key } data-workload={ 80 } onClick={ handleWorkloadChange }>
                                                    &nbsp;
                                                </td>
                                            );
                                        })
                                    ))
                                }
                            </tr>
                            <tr>
                                <td colSpan={2} className="workload-pct-label sticky2">{ '70%' }</td>
                                {
                                    planning.months.map((m: PlanningMonth) => (
                                        m.weeks.map((w: PlanningWeek) => {
                                            const mesocycle = mesocycles.find(mc => mc.weeks.includes(w.key));
                                            const isColored = 70 <= w.workload;
                                            const backgroundColor = (isColored && mesocycle) ? mesocycle.color : undefined;
                                            return (
                                                <td key={ 'workload-pct-value-70-' + w.key }
                                                    className={ 'workload-pct workload-pct-70 workload-value-' + w.workload }
                                                    style={{ backgroundColor }}
                                                    data-team={ selectedTeamId } data-season={ selectedSeasonId }
                                                    data-week={ w.key } data-workload={ 70 } onClick={ handleWorkloadChange }>
                                                    &nbsp;
                                                </td>
                                            );
                                        })
                                    ))
                                }
                            </tr>
                            <tr>
                                <td colSpan={2} className="workload-pct-label sticky2">{ '60%' }</td>
                                {
                                    planning.months.map((m: PlanningMonth) => (
                                        m.weeks.map((w: PlanningWeek) => {
                                            const mesocycle = mesocycles.find(mc => mc.weeks.includes(w.key));
                                            const isColored = 60 <= w.workload;
                                            const backgroundColor = (isColored && mesocycle) ? mesocycle.color : undefined;
                                            return (
                                                <td key={ 'workload-pct-value-60-' + w.key }
                                                    className={ 'workload-pct workload-pct-60 workload-value-' + w.workload }
                                                    style={{ backgroundColor }}
                                                    data-team={ selectedTeamId } data-season={ selectedSeasonId }
                                                    data-week={ w.key } data-workload={ 60 } onClick={ handleWorkloadChange }>
                                                    &nbsp;
                                                </td>
                                            );
                                        })
                                    ))
                                }
                            </tr>
                            <tr>
                                <td colSpan={2} className="workload-pct-label sticky2">{ '50%' }</td>
                                {
                                    planning.months.map((m: PlanningMonth) => (
                                        m.weeks.map((w: PlanningWeek) => {
                                            const mesocycle = mesocycles.find(mc => mc.weeks.includes(w.key));
                                            const isColored = 50 <= w.workload;
                                            const backgroundColor = (isColored && mesocycle) ? mesocycle.color : undefined;
                                            return (
                                                <td key={ 'workload-pct-value-50-' + w.key }
                                                    className={ 'workload-pct workload-pct-50 workload-value-' + w.workload }
                                                    style={{ backgroundColor }}
                                                    data-team={ selectedTeamId } data-season={ selectedSeasonId }
                                                    data-week={ w.key } data-workload={ 50 } onClick={ handleWorkloadChange }>
                                                    &nbsp;
                                                </td>
                                            );
                                        })
                                    ))
                                }
                            </tr>
                            <tr>
                                <td colSpan={2} className="workload-pct-label sticky2">{ '40%' }</td>
                                {
                                    planning.months.map((m: PlanningMonth) => (
                                        m.weeks.map((w: PlanningWeek) => {
                                            const mesocycle = mesocycles.find(mc => mc.weeks.includes(w.key));
                                            const isColored = 40 <= w.workload;
                                            const backgroundColor = (isColored && mesocycle) ? mesocycle.color : undefined;
                                            return (
                                                <td key={ 'workload-pct-value-40-' + w.key }
                                                    className={ 'workload-pct workload-pct-40 workload-value-' + w.workload }
                                                    style={{ backgroundColor }}
                                                    data-team={ selectedTeamId } data-season={ selectedSeasonId }
                                                    data-week={ w.key } data-workload={ 40 } onClick={ handleWorkloadChange }>
                                                    &nbsp;
                                                </td>
                                            );
                                        })
                                    ))
                                }
                            </tr>
                            <tr>
                                <td colSpan={2} className="workload-pct-label sticky2">{ '%' }</td>
                                {
                                    planning.months.map((m: PlanningMonth) => (
                                        m.weeks.map((w: PlanningWeek) => {
                                            const mesocycle = mesocycles.find(mc => mc.weeks.includes(w.key));
                                            return (
                                                <td className="workload-pct" key={ 'workload-pct-value-total-' + w.key }
                                                    style={ mesocycle && w.workload > 0 ? { backgroundColor: mesocycle.color } : undefined }>
                                                    { w.workload }
                                                </td>
                                            );
                                        })
                                    ))
                                }
                            </tr>
                            {/* Periodización Group */}
                            <tr>
                                <td rowSpan={ 6 } className="periodization-label sticky1"><div>{ t("Periodization") }</div></td>
                                <td colSpan={2} className="periodization-macrocycle-label sticky2">{ t("Macrocycle") }</td>
                                {renderMacrocycleCells()}
                            </tr>
                            <tr>
                                <td colSpan={2} className="periodization-period-label sticky2">{ t("Period") }</td>
                                {renderPeriodCells()}
                            </tr>
                            <tr>
                                <td colSpan={2} className="periodization-mesocycle-label sticky2">{ t("Mesocycle") }</td>
                                {renderMesocycleCells()}
                            </tr>
                            <tr>
                                <td colSpan={2} className="periodization-main-objective-label sticky2">{ t("Main Obj.") }</td>
                                {renderPrimaryObjectiveCells()}
                            </tr>
                            <tr>
                                <td colSpan={2} className="periodization-mesocycle-objectives-label sticky2">{ t("Mesoc. Obj.") }</td>
                                {renderMesocycleObjectivesCells()}
                            </tr>
                            <tr>
                                <td colSpan={2} className="periodization-microcycle-label sticky2">{ t("Microcycle") }</td>
                                {
                                    planning.months.map((m: PlanningMonth) => (
                                        m.weeks.map((w: PlanningWeek) => (
                                            <td key={ 'microcycle-week-label-' + w.key } className="microcycle-week-label">
                                                { w.name }
                                            </td>
                                        ))
                                    ))
                                }
                            </tr>
                            {/* Number of sessions row */}
                            <tr>
                                <td colSpan={3} className="total-sessions-label sticky1">{ t("Number of sessions") }</td>
                                {
                                    planning.months.map((m: PlanningMonth) => (
                                        m.weeks.map((w: PlanningWeek) => (
                                            <td key={ 'total-sessions-' + w.key }
                                                className="total-sessions-cell"
                                                onClick={() => handleTotalSessionsClick(w)}>
                                                { w.total_sessions && w.total_sessions > 0 ? w.total_sessions : '' }
                                            </td>
                                        ))
                                    ))
                                }
                            </tr>
                            {/* Content Distribution Group */}
                            <tr>
                                <td rowSpan={ planningLevels.filter((pl: any) => pl.type == 'water' || pl.type == 'ground').length } className="content-distribution-label sticky1"><div>{ t("Content Distribution") }</div></td>
                                <td rowSpan={ planningLevels.filter((pl: any) => pl.type == 'water').length } className="content-distribution-water-label sticky2"><div>{ t("Kilometers/week water") }</div></td>
                                <td className="content-distribution-planninglevel water sticky3">{ planningLevels.filter((pf: any) => pf.type == 'water')[0].name }</td>
                                {renderContentDistributionCells(planningLevels.filter((pf: any) => pf.type == 'water')[0].key)}
                            </tr>
                            {
                                planningLevels.filter((pf: any) => pf.type == 'water').slice(1).map((pfc: any) => (
                                    <tr key={`content-distribution-water-${pfc.key}`}>
                                        <td className="content-distribution-planninglevel water sticky3">{ pfc.name }</td>
                                        {renderContentDistributionCells(pfc.key)}
                                    </tr>
                                ))
                            }
                            <tr>
                                <td rowSpan={ planningLevels.filter((pl: any) => pl.type == 'ground').length } className="content-distribution-ground-label sticky2"><div>{ t("Hours/week ground") }</div></td>
                                <td className="content-distribution-planninglevel ground sticky3">{ planningLevels.filter((pf: any) => pf.type == 'ground')[0].name }</td>
                                {renderContentDistributionCells(planningLevels.filter((pf: any) => pf.type == 'ground')[0].key)}
                            </tr>
                            {
                                planningLevels.filter((pf: any) => pf.type == 'ground').slice(1).map((pfc: any) => (
                                    <tr key={`content-distribution-ground-${pfc.key}`}>
                                        <td className="content-distribution-planninglevel ground sticky3">{ pfc.name }</td>
                                        {renderContentDistributionCells(pfc.key)}
                                    </tr>
                                ))
                            }
                            {/* Technification Group */}
                            <tr>
                                <td rowSpan={ planningLevels.length + 4 } className="technification-label sticky1"><div>{ t('Technification') }</div></td>
                                <td colSpan={2} className="technification-microcycle-label sticky2">{ t("Microcycle") }</td>
                                {
                                    planning.months.map((m: PlanningMonth) => (
                                        m.weeks.map((w: PlanningWeek) => (
                                            <td key={ 'technification-microcycle-week-label-' + w.key } className="microcycle-week-label">
                                                { w.name }
                                            </td>
                                        ))
                                    ))
                                }
                            </tr>
                            <tr>
                                <td rowSpan={ planningLevels.filter((pl: any) => pl.type == 'water').length } className="technification-water-label sticky2"><div>{ t("Kilometers/week water") }</div></td>
                                <td className="technification-planninglevel water sticky3">{ planningLevels.filter((pf: any) => pf.type == 'water')[0].name }</td>
                                { 
                                    planning.months.map((m: PlanningMonth) => (
                                        m.weeks.map((w: PlanningWeek) => (
                                            <td key={ 'microcycle-week-value-' + w.key } className="microcycle-week-value">
                                                { getWeekValue(m.key, w.key, planningLevels.filter((pf: any) => pf.type == 'water')[0].key) }
                                            </td>
                                        ))
                                    ))
                                }
                            </tr>
                            {
                                planningLevels.filter((pf: any) => pf.type == 'water').slice(1).map((pfc: any) => (
                                    <tr>
                                        <td className="technification-planninglevel water sticky3">{ pfc.name }</td>
                                        { 
                                            planning.months.map((m: PlanningMonth) => (
                                                m.weeks.map((w: PlanningWeek) => (
                                                    <td key={ 'microcycle-week-value-' + w.key } className="microcycle-week-value">
                                                        { getWeekValue(m.key, w.key, pfc.key) }
                                                    </td>
                                                ))
                                            ))
                                        }
                                    </tr>
                                ))
                            }
                            <tr>
                                <td rowSpan={ planningLevels.filter((pl: any) => pl.type == 'ground').length } className="technification-ground-label sticky2"><div>{ t("Hours/week ground") }</div></td>
                                <td className="technification-planninglevel ground sticky3">{ planningLevels.filter((pf: any) => pf.type == 'ground')[0].name }</td>
                                { 
                                    planning.months.map((m: PlanningMonth) => (
                                        m.weeks.map((w: PlanningWeek) => (
                                            <td key={ 'microcycle-week-value-' + w.key } className="microcycle-week-value">
                                                { getWeekValue(m.key, w.key, planningLevels.filter((pf: any) => pf.type == 'ground')[0].key) }
                                            </td>
                                        ))
                                    ))
                                }
                            </tr>
                            {
                                planningLevels.filter((pf: any) => pf.type == 'ground').slice(1).map((pfc: any) => (
                                    <tr>
                                        <td className="technification-planninglevel ground sticky3">{ pfc.name }</td>
                                        { 
                                            planning.months.map((m: PlanningMonth) => (
                                                m.weeks.map((w: PlanningWeek) => (
                                                    <td key={ 'microcycle-week-value-' + w.key } className="microcycle-week-value">
                                                        { getWeekValue(m.key, w.key, pfc.key) }
                                                    </td>
                                                ))
                                            ))
                                        }
                                    </tr>
                                ))
                            }
                            {
                                <tr>
                                    <td colSpan={2} className="technification-planninglevel total water sticky2">{ t("KM water") }</td>
                                    {
                                        planning.months.map((m: PlanningMonth) => (
                                            m.weeks.map((w: PlanningWeek) => {
                                                const mesocycle = mesocycles.find(mc => mc.weeks.includes(w.key));
                                                const backgroundColor = mesocycle?.color || undefined;
                                                return (
                                                    <td key={ 'microcycle-week-value-' + w.key } className="km-water-microcycle-week-value" style={{ backgroundColor }}>
                                                        { planningLevels.filter((pf: any) => pf.type == 'water').reduce((sum: number, obj: any) => sum + getWeekValue(m.key, w.key, obj.key), 0).toFixed(1) }
                                                    </td>
                                                );
                                            })
                                        ))
                                    }
                                </tr>
                            }
                            {
                                <tr>
                                    <td colSpan={2} className="technification-planninglevel total ground sticky2">{ t("H ground") }</td>
                                    {
                                        planning.months.map((m: PlanningMonth) => (
                                            m.weeks.map((w: PlanningWeek) => {
                                                const mesocycle = mesocycles.find(mc => mc.weeks.includes(w.key));
                                                const backgroundColor = mesocycle?.color || undefined;
                                                return (
                                                    <td key={ 'microcycle-week-value-' + w.key } className="h-ground-microcycle-week-value" style={{ backgroundColor }}>
                                                        { planningLevels.filter((pf: any) => pf.type == 'ground').reduce((sum: number, obj: any) => sum + getWeekValue(m.key, w.key, obj.key), 0).toFixed(1) }
                                                    </td>
                                                );
                                            })
                                        ))
                                    }
                                </tr>
                            }
                            {
                                <tr>
                                    <td colSpan={2} className="technification-planninglevel total sticky2">{ t("H Total") }</td>
                                    {
                                        planning.months.map((m: PlanningMonth) => (
                                            m.weeks.map((w: PlanningWeek) => {
                                                return (
                                                    <td key={ 'microcycle-week-value-' + w.key } className="h-total-microcycle-week-value">
                                                        { getWeekValue(m.key, w.key, 'TH') }
                                                    </td>
                                                );
                                            })
                                        ))
                                    }
                                </tr>
                            }
                        </tbody>
                    </Table>
                </div>
                </>
            }
            { showWeekPlanner && selectedWeek &&
                <CoachPlanningWeek week={ selectedWeek } closeCallback={ handleHideWeekPlanner }
                    saveWorkload={ handleSaveWorkload } saveDay={ handleSaveDay }
                    team_id={ selectedTeamId} team_name={ selectedTeamName}
                    season_id={ selectedSeasonId} season_name={ selectedSeasonName}
                    onRefresh={ handleRefreshWeekPlanner }
                    copiedDay={ copiedDayRef }
                    onCopyDay={ setCopiedDayRef } />
            }

            <MacrocycleModal
                show={showMacrocycleModal}
                selectedWeeks={selectedWeeks}
                editingMacrocycle={editingMacrocycle}
                onSave={handleSaveMacrocycle}
                onClose={handleCloseMacrocycleModal}
            />

            <PeriodModal
                show={showPeriodModal}
                selectedWeeks={selectedWeeksPeriod}
                editingPeriod={editingPeriod}
                onSave={handleSavePeriod}
                onClose={handleClosePeriodModal}
            />

            <MesocycleModal
                show={showMesocycleModal}
                selectedWeeks={selectedWeeksMesocycle}
                editingMesocycle={editingMesocycle}
                onSave={handleSaveMesocycle}
                onClose={handleCloseMesocycleModal}
            />

            <PlanningRaceModal
                show={showRaceModal}
                week={selectedRaceWeek}
                seasonId={selectedSeasonId}
                teamId={selectedTeamId}
                onClose={handleCloseRaceModal}
                onSave={handleSaveRaces}
            />

            <ContentEditModal
                show={showContentModal}
                week={selectedContentWeek}
                fieldKey={selectedContentField}
                fieldName={selectedContentFieldName}
                existingContent={selectedContentWeek ? getContentValue(selectedContentWeek, selectedContentField) : null}
                onClose={handleCloseContentModal}
                onSave={handleSaveContent}
            />

            <TotalSessionsModal
                show={showTotalSessionsModal}
                week={selectedSessionsWeek}
                onClose={handleCloseTotalSessionsModal}
                onSave={handleSaveTotalSessions}
            />

            {/* Context menu for copy/paste/clear week */}
            {contextMenuVisible && (
                <div
                    className="week-context-menu"
                    style={{
                        position: 'fixed',
                        top: contextMenuPosition.y,
                        left: contextMenuPosition.x,
                        zIndex: 10000
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="context-menu-item" onClick={handleOpenWeeklyView}>
                        <i className="bi bi-box-arrow-up-right"></i>
                        <span>{t("Open Weekly View")}</span>
                    </div>
                    <div className="context-menu-item" onClick={handleCopyWeek}>
                        <i className="bi bi-clipboard"></i>
                        <span>{t("Copy Week")}</span>
                    </div>
                    {copiedWeekRef && (
                        <div className="context-menu-item" onClick={handlePasteWeek}>
                            <i className="bi bi-clipboard-check"></i>
                            <span>{t("Paste Week")}</span>
                        </div>
                    )}
                    <div className="context-menu-item context-menu-item-danger" onClick={handleClearWeek}>
                        <i className="bi bi-trash"></i>
                        <span>{t("Clear Week")}</span>
                    </div>
                </div>
            )}

            {/* Weekly Planning Modal */}
            {weeklyModalParams && (
                <WeeklyPlanningModal
                    show={showWeeklyModal}
                    seasonId={weeklyModalParams.seasonId}
                    teamId={weeklyModalParams.teamId}
                    weekKey={weeklyModalParams.weekKey}
                    onClose={handleCloseWeeklyModal}
                />
            )}
        </div>
    );
}

export default CoachPlanning;